import React, {useContext, useEffect} from "react";
import {Button, Card, Divider, Label, Radio, RadioGroup, Select, TextField} from "@nike/eds";
import {transportModeOptions} from "./Constants";
import {PODService} from "../../services/PODService";
import {OptionValue} from "../../shared/OptionValue";
import SnackbarContext, {SnackbarStatus} from "../../shared/providers/SnackbarContext";
import {downloadCertificateReportCsv} from "./PODCorrectionsDownload";

interface PODCertificateReportProps {
    activeTabId: string,
    podService: PODService
}

export function PODCertificateReport(props: PODCertificateReportProps) {

    const [departureDateFrom, setDepartureDateFrom] = React.useState<Date>(new Date());
    const [departureDateTo, setDepartureDateTo] = React.useState<Date>(new Date());
    const [selectedMcoCountries, setSelectedMcoCountries] = React.useState<OptionValue[]>([]);
    const [withPodNumber, setWithPodNumber] = React.useState<string>("true");
    const [selectedTransportModes, setSelectedTransportModes] = React.useState<OptionValue[]>([]);
    const [forwarderOptions, setForwarderOptions] = React.useState<OptionValue[]>([]);
    const [selectedForwarders, setSelectedForwarders] = React.useState<OptionValue[]>([]);
    const [mcoCountryOptions, setMcoCountryOptions] = React.useState<OptionValue[]>([]);
    const [hasDateRangeError, setHasDateOrRangeError] = React.useState<boolean>(false);

    const snackbarCtx = useContext(SnackbarContext);

    useEffect(() => {
        props.podService.findForwarders()
            .then(result => setForwarderOptions(result.data.findForwarders.map(fwd => ({label: fwd, value: fwd}))))
            .catch(error => {
                switch (error?.cause?.statusCode) {
                    case 401:
                        snackbarCtx.displayMsg("Your session has expired. Please log in again.", SnackbarStatus.error);
                        break
                    case 403:
                        snackbarCtx.displayMsg("It seems you are not allowed to execute this function, request group access to App.CCH.Trade.FilingSpecialist", SnackbarStatus.error, 10000);
                        break
                    default:
                        snackbarCtx.displayMsg("An error occurred while fetching forwarder data, check with your support if the issue persists.", SnackbarStatus.error);
                }
            });
        props.podService.findManufacturerCountryOfOrigins()
            .then(result => setMcoCountryOptions(result.data.findManufacturerCountryOfOrigins.map(mco => ({label: mco, value: mco}))))
            .catch(error => {
                switch (error?.cause?.statusCode) {
                    case 401:
                        snackbarCtx.displayMsg("Your session has expired. Please log in again.", SnackbarStatus.error);
                        break
                    case 403:
                        snackbarCtx.displayMsg("It seems you are not allowed to execute this function, request group access to App.CCH.Trade.FilingSpecialist", SnackbarStatus.error, 10000);
                        break
                    default:
                        snackbarCtx.displayMsg("An error occurred while fetching origin country data, check with your support if the issue persists.", SnackbarStatus.error);
                }
            });
    }, [props.podService]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const MAX_DATE_RANGE_DAYS = 62;
        setHasDateOrRangeError(
            !(departureDateFrom instanceof Date && !isNaN(departureDateFrom.getTime())) ||
            !(departureDateTo instanceof Date && !isNaN(departureDateTo.getTime())) ||
            departureDateTo < departureDateFrom ||
            (departureDateTo.getTime() - departureDateFrom.getTime()) / (1000 * 60 * 60 * 24) > MAX_DATE_RANGE_DAYS
        );
    }, [departureDateFrom, departureDateTo]);

    function generateReport() {
        props.podService.findDeliveryPreferentialOriginDocumentsByReportCriteria({
            departureDateFrom: `${departureDateFrom.toISOString().split('T')[0]}T00:00`,
            departureDateTo: `${departureDateTo.toISOString().split('T')[0]}T23:59`,
            manufacturerCountryOfOrigins: selectedMcoCountries.map(c => c.value),
            podNumberFilled: withPodNumber === "true",
            transportationModes: selectedTransportModes.map(t => t.value),
            forwarders: selectedForwarders.map(f => f.value)
        })
            .then(result => {
                if (!Array.isArray(result) || result.length === 0) {
                    snackbarCtx.displayMsg("No data found for the given criteria", SnackbarStatus.neutral);
                } else {
                    downloadCertificateReportCsv({
                        departureDateFrom: departureDateFrom.toUTCString(),
                        departureDateTo: departureDateTo.toUTCString(),
                        manufacturerCountryOfOrigins: selectedMcoCountries.map(c => c.value),
                        podNumberFilled: withPodNumber === "true",
                        transportationModes: selectedTransportModes.map(t => t.value),
                        forwarders: selectedForwarders.map(f => f.value),
                        data: result,
                        snackbarCtx
                    })
                }
            })
            .catch(error => snackbarCtx.displayMsg("Error generating report: " + error.message, SnackbarStatus.error));

    }

    return (
        <div hidden={props.activeTabId !== 'report'}>
            <Card className="eds-flex eds-flex--direction-column eds-gap--16">

                <Label className="eds-type--title-3">Report criteria</Label>
                <Divider/>
                <div className="eds-grid eds-grid--m-cols-4 eds-gap--16">

                    <Label className="eds-type--title-5">Departure Date from</Label>
                    <div className="eds-grid--col-3 eds-flex eds-flex--align-items-center eds-gap--16 reportValue">
                        <TextField
                            id="departureDateFrom"
                            label=""
                            hideLabel
                            type="date"
                            value={departureDateFrom instanceof Date && !isNaN(departureDateFrom.getTime()) ? departureDateFrom.toISOString().split('T')[0] : ''}
                            onChange={e => setDepartureDateFrom(new Date(e.target.value))}
                            hasErrors={!(departureDateFrom instanceof Date && !isNaN(departureDateFrom.getTime()))}
                            errorMessage="not a valid date"
                        />
                        <span className="eds-type--title-5 eds-flex--grow-1" style={{textAlign: "center"}}>to</span>
                        <TextField
                            id="departureDateTo"
                            label=""
                            hideLabel
                            type="date"
                            value={departureDateTo instanceof Date && !isNaN(departureDateTo.getTime()) ? departureDateTo.toISOString().split('T')[0] : ''}
                            onChange={e => setDepartureDateTo(new Date(e.target.value))}
                            hasErrors={hasDateRangeError}
                            errorMessage="'to' needs to be after 'from' and range may not exceed 2 months"
                        />
                    </div>

                    <Label className="eds-type--title-5">Manufacturer Country of Origin</Label>
                    <div className="eds-grid--col-3 reportValue">
                        <Select
                            id="mcoCountrySelect"
                            options={mcoCountryOptions}
                            label=""
                            hideLabel={true}
                            value={selectedMcoCountries}
                            isMulti={true}
                            onChange={(option) => {
                                setSelectedMcoCountries(option as OptionValue[]);
                            }}
                            hasErrors={selectedMcoCountries.length === 0}
                            errorMessage="required"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>

                    <Label className="eds-type--title-5">POD Number</Label>
                    <div className="eds-grid--col-3 reportValue">
                        <RadioGroup
                            id="podNumberFilledRadioGroup"
                            label=""
                            hideLabel={true}
                            name="podNumberFilledRadioGroup"
                            valueSelected={withPodNumber}
                            onChange={e => setWithPodNumber(e.target.value)}
                        >
                            <Radio label="filled" value="true" id="podNumberFilled"/>
                            <Radio label="empty" value="false" id="podNumberEmpty"/>
                        </RadioGroup>
                    </div>

                    <Label className="eds-type--title-5">Transportation Mode</Label>
                    <div className="eds-grid--col-3 reportValue">
                        <Select
                            id="transportModeSelect"
                            options={transportModeOptions}
                            label=""
                            hideLabel={true}
                            value={selectedTransportModes}
                            isMulti={true}
                            required
                            onChange={(option) => {
                                setSelectedTransportModes(option as OptionValue[]);
                            }}
                            hasErrors={selectedTransportModes.length === 0}
                            errorMessage="required"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>

                    <Label className="eds-type--title-5">Forwarders</Label>
                    <div className="eds-grid--col-3 reportValue">
                        <Select
                            id="forwarderSelect"
                            options={forwarderOptions}
                            label=""
                            hideLabel={true}
                            value={selectedForwarders}
                            isMulti={true}
                            onChange={(option) => {
                                setSelectedForwarders(option as OptionValue[]);
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                </div>
                <Divider/>

                <Button
                    onClick={generateReport}
                    style={{maxWidth: "175px"}}
                    size="small"
                    disabled={hasDateRangeError || selectedTransportModes.length === 0 || selectedMcoCountries.length === 0}
                >
                    Generate Report
                </Button>

            </Card>
        </div>
    );
}